import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiJson } from '../apiClient';
import { EXPENSE_CATEGORY_VALUES } from '../constants/expenseCategories';
import {
  expenseCategoryLabelForUser,
  expenseSummaryKeysForUser,
  factoryOperatingExpenseTotal,
} from '../constants/factoryExpenseCategories';
import { useLanguage } from '../i18n/LanguageContext';
import { getLocalDateKey } from '../utils/reportDates';
import { requestExpensesAddPrefs } from '../utils/navigationFocus';

function formatTsh(value) {
  return Math.round(Number(value) || 0).toLocaleString();
}

const FactoryExpensesHub = ({ user, onNavigate }) => {
  const { t, lang } = useLanguage();
  const todayKey = getLocalDateKey();
  const summaryKeys = useMemo(() => expenseSummaryKeysForUser(user, lang), [user, lang]);

  const [expenseBucketsDay, setExpenseBucketsDay] = useState({ boss: 0, staff: 0, office: 0 });
  const [expenseBucketsWeek, setExpenseBucketsWeek] = useState({ boss: 0, staff: 0, office: 0 });
  const [recentExpenses, setRecentExpenses] = useState([]);
  const [loadErr, setLoadErr] = useState('');
  const [loading, setLoading] = useState(true);

  const go = useCallback(
    (key) => {
      if (typeof onNavigate === 'function') onNavigate(key);
    },
    [onNavigate]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setLoadErr('');
    try {
      const dayQs = new URLSearchParams({ window: 'day', date: todayKey });
      const weekQs = new URLSearchParams({ window: 'week', date: todayKey });
      const [dayData, weekData, listData] = await Promise.all([
        apiJson(`/expenses/summary?${dayQs.toString()}`),
        apiJson(`/expenses/summary?${weekQs.toString()}`),
        apiJson('/expenses'),
      ]);
      setExpenseBucketsDay(dayData.buckets || { boss: 0, staff: 0, office: 0 });
      setExpenseBucketsWeek(weekData.buckets || { boss: 0, staff: 0, office: 0 });
      const rows = Array.isArray(listData.expenses) ? listData.expenses : [];
      setRecentExpenses(rows.slice(0, 10));
    } catch (e) {
      setExpenseBucketsDay({ boss: 0, staff: 0, office: 0 });
      setExpenseBucketsWeek({ boss: 0, staff: 0, office: 0 });
      setRecentExpenses([]);
      setLoadErr(e.message || t('exp.hub.loadError'));
    } finally {
      setLoading(false);
    }
  }, [todayKey, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const todayOperating = factoryOperatingExpenseTotal(expenseBucketsDay);
  const weekOperating = factoryOperatingExpenseTotal(expenseBucketsWeek);

  return (
    <section className="expenses-factory-hub" aria-label={t('exp.hub.aria')}>
      <div className="expenses-factory-hub__intro">
        <h2 className="expenses-factory-hub__title">{t('exp.hub.title')}</h2>
        <p className="expenses-factory-hub__lede">
          {t('exp.hub.lede', {
            today: formatTsh(todayOperating),
            week: formatTsh(weekOperating),
          })}
        </p>
        <button type="button" className="expenses-btn expenses-btn--secondary" onClick={() => void load()} disabled={loading}>
          {t('exp.common.refresh')}
        </button>
      </div>

      {loadErr ? (
        <p className="expenses-inline-msg expenses-inline-msg--err" role="alert">
          {loadErr}
        </p>
      ) : null}

      {loading ? <p className="expenses-empty-text">{t('exp.hub.loading')}</p> : null}

      {!loading ? (
        <>
          <div className="expenses-factory-hub__grid">
            {summaryKeys.map((meta) => {
              const dayAmt = Number(expenseBucketsDay[meta.key]) || 0;
              const weekAmt = Number(expenseBucketsWeek[meta.key]) || 0;
              return (
                <button
                  key={meta.key}
                  type="button"
                  className={`expenses-factory-hub__card expenses-factory-hub__card--${meta.key}`}
                  onClick={() => go(meta.pageKey)}
                >
                  <span className="expenses-factory-hub__card-label">{meta.shortLabel || meta.label}</span>
                  <span className="expenses-factory-hub__row">
                    <span>{t('exp.hub.today')}</span>
                    <strong>TZS {formatTsh(dayAmt)}</strong>
                  </span>
                  <span className="expenses-factory-hub__row">
                    <span>{t('exp.hub.week')}</span>
                    <strong>TZS {formatTsh(weekAmt)}</strong>
                  </span>
                  <span className="expenses-factory-hub__link">{t('exp.hub.viewList')}</span>
                </button>
              );
            })}
          </div>

          <div className="expenses-factory-hub__actions">
            <button
              type="button"
              className="expenses-factory-hub__btn expenses-factory-hub__btn--staff"
              onClick={() => {
                requestExpensesAddPrefs({ category: EXPENSE_CATEGORY_VALUES.STAFF });
                go('expenses-add');
              }}
            >
              {t('exp.hub.payStaff')}
            </button>
            <button
              type="button"
              className="expenses-factory-hub__btn expenses-factory-hub__btn--office"
              onClick={() => {
                requestExpensesAddPrefs({ category: EXPENSE_CATEGORY_VALUES.OFFICE });
                go('expenses-add');
              }}
            >
              {t('exp.hub.plantCost')}
            </button>
            <button type="button" className="expenses-factory-hub__btn expenses-factory-hub__btn--ghost" onClick={() => go('expenses-add')}>
              {t('exp.hub.otherCost')}
            </button>
          </div>

          {recentExpenses.length ? (
            <div className="expenses-factory-hub__recent">
              <h3 className="expenses-factory-hub__recent-title">{t('exp.hub.recent')}</h3>
              <ul className="expenses-factory-hub__list">
                {recentExpenses.map((e) => (
                  <li key={e.id}>
                    <span className="expenses-factory-hub__list-cat">{expenseCategoryLabelForUser(user, e.category, lang)}</span>
                    <span className="expenses-factory-hub__list-desc">{e.description}</span>
                    <span className="expenses-factory-hub__list-amt">TZS {formatTsh(e.amount)}</span>
                    <span className="expenses-factory-hub__list-date">{e.expenseDate}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
};

export default FactoryExpensesHub;
